export const PAGE_SIZE_STORAGE_KEY = 'astrophytumland:products_per_page';
export const ALLOWED_PAGE_SIZES = [16, 24, 32];
/** URL/token for "show all". Avoid bare "all" — Shopify can ignore it on /collections/all. */
export const PAGE_SIZE_ALL = 'show_all';
export const PAGE_SIZE_ALL_LIMIT = 50;
export const PAGE_SIZE_ALL_MAX_PAGES = 100;

/**
 * Sort is optional for show-all. A failed static import would kill the whole
 * page-size module (Shopify CDN often serves stale unversioned relative deps).
 */
export async function applyClientCollectionSortSafe(root, href) {
  try {
    const mod = await import('#collection-sort');
    mod.applyClientCollectionSort?.(root, href);
    return true;
  } catch {
    // fall through to relative URL (live themes without importmap)
  }
  try {
    const mod = await import('./collection-sort.js');
    mod.applyClientCollectionSort?.(root, href);
    return true;
  } catch {
    return false;
  }
}

export function parseAllowedPageSize(value) {
  if (typeof value === 'string') {
    const token = value.trim();
    // Legacy token from first release; normalize to show_all.
    if (token === PAGE_SIZE_ALL || token === 'all') return PAGE_SIZE_ALL;
  } else if (value === PAGE_SIZE_ALL) {
    return PAGE_SIZE_ALL;
  }

  const n = typeof value === 'number' ? value : Number.parseInt(String(value ?? ''), 10);
  if (!Number.isFinite(n)) return null;
  if (!ALLOWED_PAGE_SIZES.includes(n)) return null;
  // Reject "16px" / "24abc": parseInt would partially parse; require exact numeric string when string.
  if (typeof value === 'string' && String(n) !== value.trim()) return null;
  return n;
}

export function readStoredPageSize(storage) {
  if (!storage || typeof storage.getItem !== 'function') return null;
  let raw;
  try {
    raw = storage.getItem(PAGE_SIZE_STORAGE_KEY);
  } catch {
    return null;
  }
  const parsed = parseAllowedPageSize(raw);
  if (parsed == null && raw != null && raw !== '') {
    try {
      storage.removeItem(PAGE_SIZE_STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }
  return parsed;
}

export function writeStoredPageSize(storage, value) {
  if (!storage) return;
  const parsed = parseAllowedPageSize(value);
  try {
    if (parsed == null) {
      if (typeof storage.removeItem === 'function') storage.removeItem(PAGE_SIZE_STORAGE_KEY);
      return;
    }
    storage.setItem(PAGE_SIZE_STORAGE_KEY, String(parsed));
  } catch {
    /* ignore quota / private mode */
  }
}

export function buildPageSizeUrl(href, pageSize) {
  const parsed = parseAllowedPageSize(pageSize);
  const url = new URL(href);
  if (parsed == null) return url.toString();
  url.searchParams.set('page_size', String(parsed));
  url.searchParams.delete('page');
  return url.toString();
}

export function buildPaginatedCollectionUrl(href, page) {
  const url = new URL(href);
  const pageNumber = Number(page);
  if (!Number.isFinite(pageNumber) || pageNumber <= 1) {
    url.searchParams.delete('page');
  } else {
    url.searchParams.set('page', String(Math.trunc(pageNumber)));
  }
  return url.toString();
}

/**
 * Prefer a valid URL page_size (persist it). Only redirect when the URL has
 * no page_size but localStorage has a saved preference.
 * Migrates legacy ?page_size=all → ?page_size=show_all.
 */
export function syncUrlIfNeeded(href, storage) {
  const url = new URL(href);
  const rawParam = url.searchParams.get('page_size');
  const current = parseAllowedPageSize(rawParam);
  if (current != null) {
    writeStoredPageSize(storage, current);
    // Rewrite legacy "all" to show_all so Liquid can see the param.
    if (rawParam === 'all') {
      return buildPageSizeUrl(href, PAGE_SIZE_ALL);
    }
    return null;
  }
  const stored = readStoredPageSize(storage);
  if (stored == null) return null;
  return buildPageSizeUrl(href, stored);
}

export function bindDeferredCardHover(root = document) {
  if (typeof globalThis !== 'undefined' && typeof globalThis.__bindSpecimenCardHover === 'function') {
    globalThis.__bindSpecimenCardHover(root);
  }
}

export function extractSpecimenCards(fromRoot) {
  if (!fromRoot || typeof fromRoot.querySelectorAll !== 'function') return [];
  return Array.from(fromRoot.querySelectorAll('.grid.grid--4 > .specimen-card, .grid > .specimen-card'));
}

export function resolvePageSizeMode(href, container, storage) {
  const fromUrl = parseAllowedPageSize(new URL(href).searchParams.get('page_size'));
  if (fromUrl === PAGE_SIZE_ALL) return PAGE_SIZE_ALL;
  if (container?.dataset?.pageSizeMode === PAGE_SIZE_ALL || container?.dataset?.pageSizeMode === 'all') {
    return PAGE_SIZE_ALL;
  }
  if (fromUrl == null && storage) {
    const stored = readStoredPageSize(storage);
    if (stored === PAGE_SIZE_ALL) return PAGE_SIZE_ALL;
  }
  return fromUrl;
}

/**
 * Subsequent show-all fetches must use the same chunk size as page 1.
 * If Liquid missed `show_all` and rendered 16/page, fetching with
 * `page_size=show_all` (often 32/page) skips products and stops early.
 */
export function resolveLoadAllFetchHref(href, container) {
  const url = new URL(href);
  const renderedSize = Number(container?.dataset?.paginateSize);
  if (Number.isFinite(renderedSize) && renderedSize > 0) {
    url.searchParams.set('page_size', String(Math.trunc(renderedSize)));
  } else if (parseAllowedPageSize(url.searchParams.get('page_size')) !== PAGE_SIZE_ALL) {
    url.searchParams.set('page_size', PAGE_SIZE_ALL);
  }
  url.searchParams.delete('page');
  return url.toString();
}

export function shouldReloadGridForPageSize(href, container) {
  const urlSize = parseAllowedPageSize(new URL(href).searchParams.get('page_size'));
  if (urlSize == null || urlSize === PAGE_SIZE_ALL) return false;
  if (!container) return false;

  const rendered = Number(container.dataset?.paginateSize);
  if (Number.isFinite(rendered) && rendered === urlSize) return false;

  const expected = Number(container.dataset?.expectedPageSize);
  if (Number.isFinite(expected) && expected === urlSize && Number.isFinite(rendered) && rendered === urlSize) {
    return false;
  }

  return true;
}

/**
 * @param {{
 *   root?: ParentNode,
 *   locationHref?: string,
 *   fetchImpl?: typeof fetch,
 *   parseHtml?: (html: string) => Document,
 * }} [options]
 */
export async function reloadGridForPageSize(options = {}) {
  const root = options.root ?? document;
  const href = options.locationHref ?? (typeof location !== 'undefined' ? location.href : '');
  const fetchImpl = options.fetchImpl ?? (typeof fetch !== 'undefined' ? fetch.bind(globalThis) : null);
  const parseHtml = options.parseHtml ?? ((html) => new DOMParser().parseFromString(html, 'text/html'));

  const container = root.querySelector?.('[data-collection-product-grid]');
  if (!container || !fetchImpl || !shouldReloadGridForPageSize(href, container)) {
    return { reloaded: false };
  }

  const sectionId = container.dataset?.sectionId;
  const url = new URL(href);

  if (sectionId) {
    url.searchParams.set('sections', sectionId);
    const response = await fetchImpl(url.toString(), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return { reloaded: false };
    const json = await response.json();
    const sectionHtml = json[sectionId];
    if (!sectionHtml || typeof sectionHtml !== 'string') return { reloaded: false };

    const doc = parseHtml(sectionHtml);
    const parsedGrid = doc.querySelector?.('[data-collection-product-grid]');
    if (!parsedGrid) return { reloaded: false };

    const newGrid = typeof document !== 'undefined' && document.importNode
      ? document.importNode(parsedGrid, true)
      : parsedGrid;
    container.replaceWith(newGrid);
    bindDeferredCardHover(newGrid);
    return { reloaded: true, container: newGrid };
  }

  const response = await fetchImpl(href, {
    credentials: 'same-origin',
    headers: { Accept: 'text/html' },
  });
  if (!response.ok) return { reloaded: false };

  const html = await response.text();
  const parsed = parseHtml(html);
  const parsedFullGrid = parsed.querySelector?.('[data-collection-product-grid]');
  if (!parsedFullGrid) return { reloaded: false };

  const adopted = typeof document !== 'undefined' && document.importNode
    ? document.importNode(parsedFullGrid, true)
    : parsedFullGrid;
  container.replaceWith(adopted);
  bindDeferredCardHover(adopted);
  return { reloaded: true, container: adopted };
}

/**
 * @param {{
 *   root?: ParentNode,
 *   locationHref?: string,
 *   fetchImpl?: typeof fetch,
 *   parseHtml?: (html: string) => { querySelectorAll: Function },
 *   documentImpl?: { createElement: Function, importNode?: Function },
 * }} [options]
 */
export async function loadAllCollectionPages(options = {}) {
  const root = options.root ?? document;
  const href = options.locationHref ?? (typeof location !== 'undefined' ? location.href : '');
  const storage = options.storage ?? (typeof localStorage !== 'undefined' ? localStorage : null);
  const fetchImpl = options.fetchImpl ?? (typeof fetch !== 'undefined' ? fetch.bind(globalThis) : null);
  const parseHtml = options.parseHtml ?? ((html) => new DOMParser().parseFromString(html, 'text/html'));
  const documentImpl = options.documentImpl ?? (typeof document !== 'undefined' ? document : null);
  const applySort = options.applySort ?? applyClientCollectionSortSafe;

  const container = root.querySelector?.('[data-collection-product-grid]');
  if (!container) return { loadedPages: 0 };
  if (resolvePageSizeMode(href, container, storage) !== PAGE_SIZE_ALL) return { loadedPages: 0 };

  const loadHref =
    parseAllowedPageSize(new URL(href).searchParams.get('page_size')) === PAGE_SIZE_ALL
      ? href
      : buildPageSizeUrl(href, PAGE_SIZE_ALL);
  const fetchHref = resolveLoadAllFetchHref(loadHref, container);

  const grid = container.querySelector('.grid.grid--4') || container.querySelector('.grid');
  let pagination = container.querySelector('.pagination');
  if (!grid || !fetchImpl) return { loadedPages: 1 };

  const loadingLabel = container.dataset.loadingLabel || '';
  const errorLabel = container.dataset.errorLabel || '';
  const declaredPages = Number.parseInt(container.dataset.paginatePages || '', 10);
  const hasDeclaredPages = Number.isFinite(declaredPages) && declaredPages > 0;

  const ensurePaginationStatusHost = () => {
    if (pagination) return pagination;
    if (!documentImpl?.createElement || !container.appendChild) return null;
    pagination = documentImpl.createElement('nav');
    pagination.className = 'pagination';
    pagination.setAttribute('aria-label', 'Pagination');
    container.appendChild(pagination);
    return pagination;
  };

  const setPaginationStatus = (text) => {
    const host = ensurePaginationStatusHost();
    if (!host || !documentImpl?.createElement) return;
    host.hidden = false;
    if (typeof host.replaceChildren === 'function') host.replaceChildren();
    else host.innerHTML = '';
    const status = documentImpl.createElement('p');
    status.className = 'pagination__status';
    status.setAttribute('role', 'status');
    status.textContent = text;
    host.appendChild(status);
  };

  if (loadingLabel) setPaginationStatus(loadingLabel);

  let loadedPages = 1;
  try {
    for (let page = 2; page <= PAGE_SIZE_ALL_MAX_PAGES; page += 1) {
      if (hasDeclaredPages && page > declaredPages) break;

      const response = await fetchImpl(buildPaginatedCollectionUrl(fetchHref, page), {
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
      });
      if (!response.ok) throw new Error(`page ${page} failed: ${response.status}`);
      const html = await response.text();
      const doc = parseHtml(html);
      const cards = extractSpecimenCards(doc);
      if (cards.length === 0) break;

      for (const card of cards) {
        const node = documentImpl?.importNode ? documentImpl.importNode(card, true) : card;
        grid.appendChild(node);
      }
      loadedPages += 1;
    }
    if (pagination) {
      pagination.hidden = true;
      if (typeof pagination.replaceChildren === 'function') pagination.replaceChildren();
      else pagination.innerHTML = '';
    }
  } catch {
    if (errorLabel) setPaginationStatus(errorLabel);
    else if (pagination) pagination.hidden = true;
  }

  await applySort(root, href);

  bindDeferredCardHover(grid);

  return { loadedPages };
}

/**
 * @param {{
 *   root?: ParentNode,
 *   storage?: Storage,
 *   locationHref?: string,
 *   navigate?: (url: string) => void,
 *   replace?: (url: string) => void,
 *   loadAll?: typeof loadAllCollectionPages,
 *   reloadGrid?: typeof reloadGridForPageSize,
 *   force?: boolean,
 * }} [options]
 */
const initedRoots = new WeakSet();

export function initCollectionPageSize(options = {}) {
  const root = options.root ?? document;
  if (!options.force && root && typeof root === 'object') {
    if (initedRoots.has(root)) return;
    initedRoots.add(root);
  }

  const storage = options.storage ?? (typeof localStorage !== 'undefined' ? localStorage : null);
  const href = options.locationHref ?? (typeof location !== 'undefined' ? location.href : '');
  const navigate = options.navigate ?? ((url) => {
    window.location.assign(url);
  });
  const replace = options.replace ?? ((url) => {
    window.location.replace(url);
  });
  const loadAll = options.loadAll ?? loadAllCollectionPages;
  const reloadGrid = options.reloadGrid ?? reloadGridForPageSize;
  const applySort = options.applySort ?? applyClientCollectionSortSafe;

  const redirect = syncUrlIfNeeded(href, storage);
  if (redirect) {
    replace(redirect);
    return;
  }

  const urlSize = parseAllowedPageSize(new URL(href).searchParams.get('page_size'));
  const activeSize = urlSize ?? readStoredPageSize(storage);

  const bindPageSizeSelect = (select) => {
    if (select.dataset?.pageSizeBound === 'true') return;
    if (select.dataset) {
      select.dataset.pageSizeBound = 'true';
      select.dataset.controlsBound = 'true';
    }
    if (typeof select.removeAttribute === 'function') {
      select.removeAttribute('onchange');
    }

    if (activeSize != null) {
      select.value = String(activeSize);
    }
    select.addEventListener('change', (e) => {
      const nextSize = parseAllowedPageSize(e.target.value);
      if (nextSize == null) return;
      writeStoredPageSize(storage, nextSize);

      const form = select.closest?.('[data-collection-page-size-form]');
      if (form && typeof form.requestSubmit === 'function' && nextSize !== PAGE_SIZE_ALL) {
        form.requestSubmit();
        return;
      }

      const currentHref = typeof window !== 'undefined' ? window.location.href : href;
      navigate(buildPageSizeUrl(currentHref, nextSize));
    });
  };

  root.querySelectorAll('[data-collection-page-size]').forEach(bindPageSizeSelect);

  const container = root.querySelector?.('[data-collection-product-grid]');

  const continueAfterGrid = () => {
    const loadAllHref =
      activeSize === PAGE_SIZE_ALL && urlSize !== PAGE_SIZE_ALL
        ? buildPageSizeUrl(href, PAGE_SIZE_ALL)
        : href;

    if (activeSize === PAGE_SIZE_ALL) {
      void loadAll({ root, locationHref: loadAllHref, applySort, storage }).then(() => {
        void applySort(root, typeof window !== 'undefined' ? window.location.href : href);
      });
    } else {
      void applySort(root, href);
    }
  };

  if (container && shouldReloadGridForPageSize(href, container)) {
    void reloadGrid({ root, locationHref: href }).then((result) => {
      if (result?.reloaded && result.container) {
        root.querySelectorAll('[data-collection-page-size]').forEach(bindPageSizeSelect);
      }
      continueAfterGrid();
    });
    return;
  }

  continueAfterGrid();
}

/**
 * Direct <script type="module"> entry (no importmap required for this file).
 * Safe to also call via collection-boot — init is idempotent per root.
 */
export function autoInitCollectionPageSize(documentImpl = typeof document !== 'undefined' ? document : null) {
  if (!documentImpl?.querySelector?.('[data-collection-page-size]')) return false;
  initCollectionPageSize({ root: documentImpl });
  return true;
}

if (typeof document !== 'undefined') {
  const run = () => {
    autoInitCollectionPageSize(document);
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  } else {
    run();
  }
}
